import { Tooltip } from 'antd';
import React, { memo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useMutation, useRelayEnvironment } from 'react-relay';

import AccessContainer from '../components/AccessContainer';
import { HEIGHT_ITEM } from '../const';
import { SettingTypeInput } from '../graphql/__generated__/UpdateSettingMutation.graphql';
import UpdateRemoteSettingMutation from '../graphql/mutations/UpdateRemoteSettingMutation';
import useCommitSetting from '../hooks/useCommitSetting';
import { ComponentsDispatch, RootState } from '../store';
import { addSpinnerItem, removeSpinnerItem } from '../store/slices/spinnerSlice';
import { TaskBoardTaskAssignee } from '../taskBoard/component/taskBoardTaskAssignee';
import { SettingGantType } from '../types';
import GantItemControlEditLabelUser from './gantItemControlEditLabelUser';

interface IGantItemControlEdit {
    projectId: string;
    onClickToStart?: () => void;
}

const GantItemControlEdit: React.FC<IGantItemControlEdit> = ({ projectId, onClickToStart }) => {
    const dispatch = useDispatch<ComponentsDispatch>();
    const [commitUpdate] = useMutation(UpdateRemoteSettingMutation);
    const environment = useRelayEnvironment();
    const { getCommitSetting } = useCommitSetting();
    const connectionSettingGant = useSelector<RootState, string>((state) => state.gant.connectionSettingGant);
    const settingGant = useSelector<RootState, SettingGantType | null>((state) => state.gant.settingGant);
    const authorId = useSelector<RootState, string>((state) => state.gant.authorId);

    const [renderUpdate, setRenderUpdate] = useState<number>(1);

    const listSettingEditScheduleItem = settingGant?.data?.gantEditScheduleItem?.filter(
        (unit) => unit.projectId === projectId,
    );

    if (listSettingEditScheduleItem?.length && listSettingEditScheduleItem?.length > 1) {
        console.log('listSettingEditScheduleItem', listSettingEditScheduleItem);
        throw new Error('Find more the one project in edit setting');
    }

    const [settingEditScheduleItem] = listSettingEditScheduleItem || [];

    const userIdWhoIsEditProject = settingEditScheduleItem?.userId;

    const isAuthorEdit = settingEditScheduleItem?.userId === authorId;

    const isOtherAuthorEdit = settingEditScheduleItem && settingEditScheduleItem?.userId !== authorId;

    const switchEditMode = (direction: 'openEditScheduleItem' | 'closeEditScheduleItem') => {
        const idSpinner = `changeSettingForEditing${projectId}`;
        dispatch(addSpinnerItem(idSpinner));
        const newSettingGantData = {
            ...settingGant?.data,
            [direction]: {
                userId: authorId,
                projectId: projectId,
            },
        };

        const newSettingGantForServer = {
            ...settingGant,
            data: JSON.stringify(newSettingGantData),
        } as SettingTypeInput;

        commitUpdate(
            getCommitSetting(
                newSettingGantForServer,
                connectionSettingGant,
                settingGant?.id,
                () => {
                    dispatch(removeSpinnerItem(idSpinner));
                },
                true,
            ),
        );
    };

    const onClickToOpenEdit = (e: React.MouseEvent<SVGRectElement>) => {
        e.preventDefault();
        e.stopPropagation();
        switchEditMode('openEditScheduleItem');
    };

    const onClickToCloseEdit = (e: React.MouseEvent<SVGRectElement>) => {
        e.preventDefault();
        e.stopPropagation();
        switchEditMode('closeEditScheduleItem');
    };

    const getRectForClickingOrBackground = (onClickToStart?: (e: React.MouseEvent<SVGRectElement>) => void) => (
        <rect
            x={0}
            y={0}
            width={20}
            height={HEIGHT_ITEM - 10}
            fill={'white'}
            cursor="pointer"
            opacity={onClickToStart ? 0.001 : 1}
            onClick={onClickToStart}
        />
    );

    const getTextToolTip = () => {
        if (isAuthorEdit) {
            return 'Click to finish editing';
        }
        if (isOtherAuthorEdit) {
            return <GantItemControlEditLabelUser userIdWhoIsEditProject={userIdWhoIsEditProject} />;
        }
        if (!isAuthorEdit && !isOtherAuthorEdit) {
            return 'Click to start edit';
        }
    };

    const iconEdit = (
        <>
            {getRectForClickingOrBackground(onClickToStart)}
            <path
                d="M257.7 752c2 0 4-.2 6-.5L431.9 722c2-.4 3.9-1.3 5.3-2.8l423.9-423.9a9.96 9.96 0 000-14.1L694.9 114.9c-1.9-1.9-4.4-2.9-7.1-2.9s-5.2 1-7.1 2.9L256.8 538.8c-1.5 1.5-2.4 3.3-2.8 5.3l-29.5 168.2a33.5 33.5 0 009.4 29.8c6.6 6.4 14.9 9.9 23.8 9.9zm67.4-174.4L687.8 215l73.3 73.3-362.7 362.6-88.9 15.7 15.6-89zM880 836H144c-17.7 0-32 14.3-32 32v36c0 4.4 3.6 8 8 8h784c4.4 0 8-3.6 8-8v-36c0-17.7-14.3-32-32-32z"
                transform="scale(0.02)"
            />
            <Tooltip title={getTextToolTip()}>{getRectForClickingOrBackground(onClickToOpenEdit)}</Tooltip>
        </>
    );

    const iconBlock = (
        <>
            {getRectForClickingOrBackground(onClickToStart)}
            <path
                d="M832 464h-68V240c0-70.7-57.3-128-128-128H388c-70.7 0-128 57.3-128 128v224h-68c-17.7 0-32 14.3-32 32v384c0 17.7 14.3 32 32 32h640c17.7 0 32-14.3 32-32V496c0-17.7-14.3-32-32-32zM332 240c0-30.9 25.1-56 56-56h248c30.9 0 56 25.1 56 56v224H332V240zm460 600H232V536h560v304zM484 701v53c0 4.4 3.6 8 8 8h40c4.4 0 8-3.6 8-8v-53a48.01 48.01 0 10-56 0z"
                transform="scale(0.02)"
            />
            {userIdWhoIsEditProject && <TaskBoardTaskAssignee userId={userIdWhoIsEditProject} />}
            <Tooltip
                title={getTextToolTip()}
                onOpenChange={(visible) => {
                    if (visible) {
                        setRenderUpdate(renderUpdate + 1);
                    }
                }}
            >
                {getRectForClickingOrBackground(() => {})}
            </Tooltip>
        </>
    );

    const iconStop = (
        <>
            {getRectForClickingOrBackground(onClickToStart)}
            <path
                // d="M512 64C264.6 64 64 264.6 64 512s200.6 448 448 448 448-200.6 448-448S759.4 64 512 64zm0 820c-205.4 0-372-166.6-372-372 0-89 31.3-170.8 83.5-234.8l523.3 523.3C682.8 852.7 601 884 512 884zm288.5-137.2L277.2 223.5C341.2 171.3 423 140 512 140c205.4 0 372 166.6 372 372 0 89-31.3 170.8-83.5 234.8z"
                d="M699 353h-46.9c-10.2 0-19.9 4.9-25.9 13.3L469 584.3l-71.2-98.8c-6-8.3-15.6-13.3-25.9-13.3H325c-6.5 0-10.3 7.4-6.5 12.7l124.6 172.8a31.8 31.8 0 0051.7 0l210.6-292c3.9-5.3.1-12.7-6.4-12.7z"
                transform="scale(0.02)"
            />
            <path
                d="M512 64C264.6 64 64 264.6 64 512s200.6 448 448 448 448-200.6 448-448S759.4 64 512 64zm0 820c-205.4 0-372-166.6-372-372s166.6-372 372-372 372 166.6 372 372-166.6 372-372 372z"
                transform="scale(0.02)"
            />
            <Tooltip title={getTextToolTip()}>{getRectForClickingOrBackground(onClickToCloseEdit)}</Tooltip>
        </>
    );

    return (
        <AccessContainer accessFor={['work-item.create', 'work-item.edit', 'work-item.manage']}>
            {isAuthorEdit && iconStop}
            {isOtherAuthorEdit && iconBlock}
            {!isAuthorEdit && !isOtherAuthorEdit && iconEdit}
        </AccessContainer>
    );
};

export default memo(GantItemControlEdit);
